import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
	Deposit, 
	DepositCreateDto, 
	DepositBackendResponse,
	DepositState, 
	DepositRule, 
	DepositRuleCreateDto, 
	DepositRuleUpdateDto,
	DepositException,
	DepositExceptionCreateDto,
	DepositExceptionUpdateDto,
	DepositRuleBackendResponse,
	DepositExceptionBackendResponse
} from "../../modules/shared/model/deposit.interfaces";
import { 
	createDeposit as createDepositAction, 
	updateDeposit as updateDepositAction, 
	getDepositById as getDepositByIdAction, 
	getDeposits as getDepositsAction, 
	deleteDeposit as deleteDepositAction, 
	getDepositStats as getDepositStatsAction,
	createDepositRule as createDepositRuleAction,
	updateDepositRule as updateDepositRuleAction,
	getDepositRuleById as getDepositRuleByIdAction,
	getDepositRules as getDepositRulesAction,
	deleteDepositRule as deleteDepositRuleAction,
	createDepositException as createDepositExceptionAction,
	updateDepositException as updateDepositExceptionAction,
	getDepositExceptionById as getDepositExceptionByIdAction,
	getDepositExceptions as getDepositExceptionsAction,
	deleteDepositException as deleteDepositExceptionAction
} from "../Actions/DepositActions";

// Main Deposit Async Thunks
export const createDeposit = createAsyncThunk("deposits/create", async (data: DepositCreateDto, { rejectWithValue }) => {
	try { 
		return await createDepositAction(data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to create deposit"); 
	}
});

export const updateDeposit = createAsyncThunk("deposits/update", async ({ id, data }: { id: string; data: Partial<DepositCreateDto> }, { rejectWithValue }) => {
	try { 
		return await updateDepositAction(id, data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to update deposit"); 
	}
});

export const fetchDeposit = createAsyncThunk("deposits/getById", async (id: string, { rejectWithValue }) => {
	try { 
		return await getDepositByIdAction(id); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch deposit"); 
	}
});

export const fetchDeposits = createAsyncThunk("deposits/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { 
		return await getDepositsAction(query); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch deposits"); 
	}
});

export const deleteDeposit = createAsyncThunk("deposits/delete", async (id: string, { rejectWithValue }) => {
	try { 
		await deleteDepositAction(id); 
		return id; 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to delete deposit"); 
	}
});

export const fetchDepositStats = createAsyncThunk("deposits/stats", async (_, { rejectWithValue }) => {
	try { 
		return await getDepositStatsAction(); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch deposit stats"); 
	}
});

// Deposit Rule Async Thunks
export const createDepositRule = createAsyncThunk("depositRules/create", async (data: DepositRuleCreateDto, { rejectWithValue }) => {
	try { 
		return await createDepositRuleAction(data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to create deposit rule"); 
	}
});

export const updateDepositRule = createAsyncThunk("depositRules/update", async ({ id, data }: { id: string; data: DepositRuleUpdateDto }, { rejectWithValue }) => {
	try { 
		return await updateDepositRuleAction(id, data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to update deposit rule"); 
	}
});

export const fetchDepositRule = createAsyncThunk("depositRules/getById", async (id: string, { rejectWithValue }) => {
	try { 
		return await getDepositRuleByIdAction(id); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch deposit rule"); 
	}
});

export const fetchDepositRules = createAsyncThunk("depositRules/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { 
		return await getDepositRulesAction(query); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch deposit rules"); 
	}
});

export const deleteDepositRule = createAsyncThunk("depositRules/delete", async (id: string, { rejectWithValue }) => {
	try { 
		await deleteDepositRuleAction(id); 
		return id; 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to delete deposit rule"); 
	}
});

// Deposit Exception Async Thunks
export const createDepositException = createAsyncThunk("depositExceptions/create", async (data: DepositExceptionCreateDto, { rejectWithValue }) => {
	try { 
		return await createDepositExceptionAction(data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to create deposit exception"); 
	}
});

export const updateDepositException = createAsyncThunk("depositExceptions/update", async ({ id, data }: { id: string; data: DepositExceptionUpdateDto }, { rejectWithValue }) => {
	try { 
		return await updateDepositExceptionAction(id, data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to update deposit exception"); 
	}
});

export const fetchDepositException = createAsyncThunk("depositExceptions/getById", async (id: string, { rejectWithValue }) => {
	try { 
		return await getDepositExceptionByIdAction(id); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch deposit exception"); 
	}
});

export const fetchDepositExceptions = createAsyncThunk("depositExceptions/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { 
		return await getDepositExceptionsAction(query); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch deposit exceptions"); 
	}
});

export const deleteDepositException = createAsyncThunk("depositExceptions/delete", async (id: string, { rejectWithValue }) => {
	try { 
		await deleteDepositExceptionAction(id); 
		return id; 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to delete deposit exception"); 
	}
});

const initialState: DepositState = {
	items: [],
	current: null,
	loading: false,
	error: null,
	totalCount: 0,
	depositRules: [],
	depositRulesLoading: false,
	depositRulesError: null,
	depositExceptions: [],
	depositExceptionsLoading: false,
	depositExceptionsError: null,
};

const depositSlice = createSlice({
	name: "deposits",
	initialState,
	reducers: {
		clearDepositError(state) {
			state.error = null;
		},
		clearCurrentDeposit(state) {
			state.current = null;
		},
		clearDepositRulesError(state) {
			state.depositRulesError = null;
		},
		clearDepositExceptionsError(state) {
			state.depositExceptionsError = null;
		},
	},
	extraReducers: (builder) => {
		// Main Deposit Reducers
		builder
			.addCase(createDeposit.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(createDeposit.fulfilled, (s, a: PayloadAction<DepositBackendResponse>) => { 
				s.loading = false; 
				s.items.unshift(a.payload); 
				s.totalCount = (s.totalCount || 0) + 1; 
			})
			.addCase(createDeposit.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(updateDeposit.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(updateDeposit.fulfilled, (s, a: PayloadAction<DepositBackendResponse>) => {
				s.loading = false;
				const i = s.items.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.items[i] = a.payload;
				if (s.current?.id === a.payload.id) s.current = a.payload;
			})
			.addCase(updateDeposit.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(fetchDeposit.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(fetchDeposit.fulfilled, (s, a: PayloadAction<DepositBackendResponse>) => { 
				s.loading = false; 
				s.current = a.payload; 
			})
			.addCase(fetchDeposit.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(fetchDeposits.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(fetchDeposits.fulfilled, (s, a: PayloadAction<{ data: DepositBackendResponse[]; totalCount?: number }>) => { 
				s.loading = false; 
				s.items = a.payload.data || []; 
				s.totalCount = a.payload.totalCount || 0; 
			})
			.addCase(fetchDeposits.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(deleteDeposit.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(deleteDeposit.fulfilled, (s, a: PayloadAction<string>) => { 
				s.loading = false; 
				s.items = s.items.filter(x => x.id !== a.payload); 
				if (s.current?.id === a.payload) s.current = null; 
				s.totalCount = Math.max(0, (s.totalCount || 0) - 1); 
			})
			.addCase(deleteDeposit.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		// Deposit Rule Reducers
		builder
			.addCase(createDepositRule.pending, (s) => { 
				s.depositRulesLoading = true; 
				s.depositRulesError = null; 
			})
			.addCase(createDepositRule.fulfilled, (s, a: PayloadAction<DepositRuleBackendResponse>) => { 
				s.depositRulesLoading = false; 
				s.depositRules.unshift(a.payload); 
			})
			.addCase(createDepositRule.rejected, (s, a: any) => { 
				s.depositRulesLoading = false; 
				s.depositRulesError = a.payload as string; 
			});

		builder
			.addCase(updateDepositRule.pending, (s) => { 
				s.depositRulesLoading = true; 
				s.depositRulesError = null; 
			})
			.addCase(updateDepositRule.fulfilled, (s, a: PayloadAction<DepositRuleBackendResponse>) => {
				s.depositRulesLoading = false;
				const i = s.depositRules.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.depositRules[i] = a.payload;
			})
			.addCase(updateDepositRule.rejected, (s, a: any) => { 
				s.depositRulesLoading = false; 
				s.depositRulesError = a.payload as string; 
			});

		builder
			.addCase(fetchDepositRule.pending, (s) => { 
				s.depositRulesLoading = true; 
				s.depositRulesError = null; 
			})
			.addCase(fetchDepositRule.fulfilled, (s, a: PayloadAction<DepositRuleBackendResponse>) => { 
				s.depositRulesLoading = false; 
				// Update or add the deposit rule
				const existingIndex = s.depositRules.findIndex(dr => dr.id === a.payload.id);
				if (existingIndex !== -1) {
					s.depositRules[existingIndex] = a.payload;
				} else {
					s.depositRules.push(a.payload);
				}
			})
			.addCase(fetchDepositRule.rejected, (s, a: any) => { 
				s.depositRulesLoading = false; 
				s.depositRulesError = a.payload as string; 
			});

		builder
			.addCase(fetchDepositRules.pending, (s) => { 
				s.depositRulesLoading = true; 
				s.depositRulesError = null; 
			})
			.addCase(fetchDepositRules.fulfilled, (s, a: PayloadAction<{ data: DepositRuleBackendResponse[]; totalCount?: number }>) => { 
				s.depositRulesLoading = false; 
				s.depositRules = a.payload.data || []; 
			})
			.addCase(fetchDepositRules.rejected, (s, a: any) => { 
				s.depositRulesLoading = false; 
				s.depositRulesError = a.payload as string; 
			});

		builder
			.addCase(deleteDepositRule.pending, (s) => { 
				s.depositRulesLoading = true; 
				s.depositRulesError = null; 
			})
			.addCase(deleteDepositRule.fulfilled, (s, a: PayloadAction<string>) => { 
				s.depositRulesLoading = false; 
				s.depositRules = s.depositRules.filter(dr => dr.id !== a.payload); 
			})
			.addCase(deleteDepositRule.rejected, (s, a: any) => { 
				s.depositRulesLoading = false; 
				s.depositRulesError = a.payload as string; 
			});

		// Deposit Exception Reducers
		builder
			.addCase(createDepositException.pending, (s) => { 
				s.depositExceptionsLoading = true; 
				s.depositExceptionsError = null; 
			})
			.addCase(createDepositException.fulfilled, (s, a: PayloadAction<DepositExceptionBackendResponse>) => { 
				s.depositExceptionsLoading = false; 
				s.depositExceptions.unshift(a.payload); 
			})
			.addCase(createDepositException.rejected, (s, a: any) => { 
				s.depositExceptionsLoading = false; 
				s.depositExceptionsError = a.payload as string; 
			});

		builder
			.addCase(updateDepositException.pending, (s) => { 
				s.depositExceptionsLoading = true; 
				s.depositExceptionsError = null; 
			})
			.addCase(updateDepositException.fulfilled, (s, a: PayloadAction<DepositExceptionBackendResponse>) => {
				s.depositExceptionsLoading = false;
				const i = s.depositExceptions.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.depositExceptions[i] = a.payload;
			})
			.addCase(updateDepositException.rejected, (s, a: any) => { 
				s.depositExceptionsLoading = false; 
				s.depositExceptionsError = a.payload as string; 
			});

		builder
			.addCase(fetchDepositException.pending, (s) => { 
				s.depositExceptionsLoading = true; 
				s.depositExceptionsError = null; 
			})
			.addCase(fetchDepositException.fulfilled, (s, a: PayloadAction<DepositExceptionBackendResponse>) => { 
				s.depositExceptionsLoading = false; 
				// Update or add the deposit exception
				const existingIndex = s.depositExceptions.findIndex(de => de.id === a.payload.id);
				if (existingIndex !== -1) {
					s.depositExceptions[existingIndex] = a.payload;
				} else {
					s.depositExceptions.push(a.payload);
				}
			})
			.addCase(fetchDepositException.rejected, (s, a: any) => { 
				s.depositExceptionsLoading = false; 
				s.depositExceptionsError = a.payload as string; 
			});

		builder
			.addCase(fetchDepositExceptions.pending, (s) => { 
				s.depositExceptionsLoading = true; 
				s.depositExceptionsError = null; 
			})
			.addCase(fetchDepositExceptions.fulfilled, (s, a: PayloadAction<{ data: DepositExceptionBackendResponse[]; totalCount?: number }>) => { 
				s.depositExceptionsLoading = false; 
				s.depositExceptions = a.payload.data || []; 
			})
			.addCase(fetchDepositExceptions.rejected, (s, a: any) => { 
				s.depositExceptionsLoading = false; 
				s.depositExceptionsError = a.payload as string; 
			});

		builder
			.addCase(deleteDepositException.pending, (s) => { 
				s.depositExceptionsLoading = true; 
				s.depositExceptionsError = null; 
			})
			.addCase(deleteDepositException.fulfilled, (s, a: PayloadAction<string>) => { 
				s.depositExceptionsLoading = false; 
				s.depositExceptions = s.depositExceptions.filter(de => de.id !== a.payload); 
			})
			.addCase(deleteDepositException.rejected, (s, a: any) => { 
				s.depositExceptionsLoading = false; 
				s.depositExceptionsError = a.payload as string; 
			});
	}
});

export const { clearDepositError, clearCurrentDeposit, clearDepositRulesError, clearDepositExceptionsError } = depositSlice.actions;
export default depositSlice.reducer;

